import { Add, ExpandLess, ExpandMore, History, LocalShipping, PhotoAlbumOutlined, PhotoCamera, Search, Settings } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ColDef, ValueGetterParams } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import { AdminPageFrame } from 'components/AdminPageFrame'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { StatusChip } from 'components/StatusChip'
import { TOUCH_MIN, type StatusTone } from 'core/theme.tokens'
import { CreatePhotoUploadDialog } from 'components/PhotoUploadDialog'
import { usePhotoUploads, UserPhotoUploadSummary } from 'hooks/admin/usePhotoUploads'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { PhotoUploadHistory } from './components/PhotoUploadHistory'
import ReadOnlyVehicleForm from '@/components/inventory/forms/ReadOnlyVehicleForm'
import { PhotoUploadOverview } from './components/PhotoUploadOverview'
import ManageAccessCodesDialog from './components/dialogs/ManageAccessCodesDialog'
import ViewPhotoSubmissionsDialog from './components/dialogs/ViewPhotoSubmissionsDialog'

type StatusFilter = 'all' | 'not_submitted' | 'partially_submitted' | 'complete'

interface DetailGridParams {
  data: UserPhotoUploadSummary
  successCallback: (data: UserPhotoUploadSummary['vehicles']) => void
}

const REQUIRED_PHOTOS = 14

const photoUploadStatusTone = (status: string): StatusTone => {
  if (status === 'Complete' || status === 'Fully submitted') return 'success'
  if (status === 'Partially submitted') return 'warning'
  return 'danger'
}

const PhotoUpload = () => {
  const isMobile = useIsMobile()

  // Local state for the photo upload management page
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedUserSummary, setSelectedUserSummary] = useState<UserPhotoUploadSummary | null>(null)
  const [selectedPhotoUploadId, setSelectedPhotoUploadId] = useState<string | null>(null)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isInitiateDialogOpen, setIsInitiateDialogOpen] = useState(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false)
  const [vehicleDialogId, setVehicleDialogId] = useState<string | null>(null)
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false)
  const [collapsedUploadIds, setCollapsedUploadIds] = useState<Set<string>>(new Set())

  // ag-Grid API ref to control master-detail expansion
  const gridApiRef = useRef<any>(null)

  // Use the new simplified photo uploads hook
  const { photoUploads, isLoading, error, deletePhotoUpload, isDeletingPhotoUpload } = usePhotoUploads()

  const calculateProgress = useCallback((upload: UserPhotoUploadSummary): number => {
    // Prefer vehicle-complete metric (14 photos + 1 video)
    if (upload.vehiclesCompleteCount !== undefined && upload.totalVehicles > 0) {
      return Math.round((upload.vehiclesCompleteCount / upload.totalVehicles) * 100)
    }
    // Fallback to photo progress when vehicle-complete not available
    if ('photoProgress' in upload && upload.photoProgress !== undefined) {
      return upload.photoProgress
    }
    return upload.totalVehicles > 0 ? Math.round((upload.submissionCount / upload.totalVehicles) * 100) : 0
  }, [])

  const getStatusColor = useCallback((status: string): 'success' | 'warning' | 'error' => {
    if (status === 'Complete' || status === 'Fully submitted') return 'success'
    if (status === 'Partially submitted') return 'warning'
    return 'error'
  }, [])

  const handleViewPhotos = useCallback((photoUploadId: string) => {
    setSelectedPhotoUploadId(photoUploadId)
    setIsViewDialogOpen(true)
  }, [])

  const handleManageAccess = useCallback((userSummary: UserPhotoUploadSummary) => {
    setSelectedUserSummary(userSummary)
    setIsManageDialogOpen(true)
  }, [])

  // Optimize filtering by combining operations and using early returns
  const filteredPhotoUploads = useMemo(() => {
    if (!photoUploads.length) return []
    
    const searchLower = searchTerm.toLowerCase()
    const filtered = photoUploads.filter(upload => {
      // Early return for status filter
      if (statusFilter !== 'all') {
        const status = upload.overallStatus
        if (
          (statusFilter === 'not_submitted' && status !== 'Not submitted') ||
          (statusFilter === 'partially_submitted' && status !== 'Partially submitted') ||
          (statusFilter === 'complete' && status !== 'Complete' && status !== 'Fully submitted')
        ) {
          return false
        }
      }

      // Only perform search if there's a search term
      if (!searchTerm) return true

      // Combine all searchable fields for a single includes check
      return (
        upload.user.email?.toLowerCase().includes(searchLower) ||
        upload.user.phone?.toLowerCase().includes(searchLower) ||
        upload.vehicles?.some(
          vehicle =>
            `${vehicle.make} ${vehicle.model} ${vehicle.year} ${vehicle.vin} ${vehicle.stock_number || ''} ${(vehicle as any).who || ''}`
              .toLowerCase()
              .includes(searchLower)
        )
      )
    })

    // Sort: newest created first, then not submitted first
    return filtered.sort((a, b) => {
      // First, sort by creation date (newest first)
      const aCreated = new Date(a.photoUploadDocument.createdAt || 0).getTime()
      const bCreated = new Date(b.photoUploadDocument.createdAt || 0).getTime()
      if (aCreated !== bCreated) {
        return bCreated - aCreated // Newest first
      }

      // Then sort by status priority: Not submitted, Partially submitted, Complete
      const statusPriority = {
        'Not submitted': 0,
        'Partially submitted': 1,
        'Complete': 2,
        'Fully submitted': 2
      }
      const aPriority = statusPriority[a.overallStatus] ?? 3
      const bPriority = statusPriority[b.overallStatus] ?? 3
      return aPriority - bPriority
    })
  }, [photoUploads, searchTerm, statusFilter])

  const ContactCellRenderer = useCallback((params: { data: UserPhotoUploadSummary }) => (
    <Box>
      <Typography 
        variant='body2' 
        sx={{ 
          fontWeight: 500, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap', 
          maxWidth: { xs: 120, sm: 180 } 
        }}
      >
        {params.data.user.email}
      </Typography>
      {params.data.user.phone && (
        <Typography 
          variant='caption' 
          color='text.secondary' 
          sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap', 
            maxWidth: { xs: 120, sm: 180 } 
          }}
        >
          {params.data.user.phone}
        </Typography>
      )}
    </Box>
  ), [])

  const VehiclesCellRenderer = useCallback((params: { data: UserPhotoUploadSummary }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LocalShipping sx={{ fontSize: '1rem', color: 'text.secondary' }} />
      <Typography variant='body2'>{params.data?.vehicles?.length || 0}</Typography>
    </Box>
  ), [])

  const StatusCellRenderer = useCallback((params: { data: UserPhotoUploadSummary }) => {
    const status = params.data.overallStatus
    const color = getStatusColor(status)
    const vehiclesComplete = params.data.vehiclesCompleteCount || 0
    const totalVehicles = params.data.totalVehicles || 0
    const label = totalVehicles > 0 ? `${status} • ${vehiclesComplete}/${totalVehicles} complete` : status
    return <Chip size='small' label={label} color={color} sx={{ fontSize: '0.75rem', height: 24 }} />
  }, [getStatusColor])

  const ProgressCellRenderer = useCallback((params: { data: any }) => {
    const progress = calculateProgress(params.data)
    return (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
          color={progress === 100 ? 'success' : progress > 0 ? 'primary' : 'inherit'}
        />
        <Typography variant='caption' sx={{ minWidth: 35, textAlign: 'right', fontSize: '0.75rem' }}>
          {progress}%
        </Typography>
      </Box>
    )
  }, [calculateProgress])

  const PhotosCellRenderer = useCallback((params: { data: any }) => {
    const actualCount =
      params.data.actualPhotoCount !== undefined ? params.data.actualPhotoCount : params.data.submissionCount
    const expectedCount =
      params.data.expectedPhotoCount !== undefined ? params.data.expectedPhotoCount : params.data.totalVehicles
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
        <PhotoCamera sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        {actualCount}/{expectedCount}
      </Box>
    )
  }, [])

  const VideosCellRenderer = useCallback((params: { data: any }) => {
    const actualVideoCount = params.data.actualVideoCount || 0
    const expectedVideoCount = params.data.expectedVideoCount || params.data.totalVehicles
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
        <PhotoAlbumOutlined sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        {actualVideoCount}/{expectedVideoCount}
      </Box>
    )
  }, [])

  const AccessCodesSummaryRenderer = useCallback((props: { data: UserPhotoUploadSummary }) => {
    const userSummary = props.data
    const photoUploadDoc = userSummary.photoUploadDocument
    const isCompleted = photoUploadDoc?.status === 'completed'
    
    // Check if any access codes are still valid
    const now = new Date()
    const hasValidAccessCode = userSummary.accessCodes?.some(
      accessCode => new Date(accessCode.expires) > now
    ) || false
    
    // All access codes expired if we have codes but none are valid
    const allAccessCodesExpired = userSummary.accessCodes?.length > 0 && !hasValidAccessCode
    
    let label = 'No Access Code'
    let color: 'default' | 'primary' | 'error' = 'default'
    
    if (isCompleted) {
      label = 'Access Code Inactive'
      color = 'default'
    } else if (allAccessCodesExpired) {
      label = 'Access Code Expired'
      color = 'error'
    } else if (hasValidAccessCode) {
      label = 'Access Code Active'
      color = 'primary'
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Chip
          size='small'
          label={label}
          color={color}
          onClick={() => handleManageAccess(props.data)}
          sx={{ 
            fontSize: '0.75rem', 
            height: 24,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: 1,
            },
            '&:active': {
              transform: 'scale(0.98)'
            }
          }}
          aria-label="Manage access code"
        />
      </Box>
    )
  }, [handleManageAccess])

  const ActionsRenderer = useCallback((params: { data: UserPhotoUploadSummary }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Button
        variant='outlined'
        size='small'
        startIcon={<PhotoAlbumOutlined />}
        onClick={() => handleViewPhotos(params.data.photoUploadId)}
        disabled={!params.data.vehicles?.length}
        sx={{ fontSize: '0.75rem' }}
        aria-label="View photos"
      >
        View Photos
      </Button>
    </Box>
  ), [handleViewPhotos])

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'user.email',
        headerName: 'Contact',
        flex: 2.5,
        minWidth: 200,
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRenderer: ContactCellRenderer,
        },
      },
      {
        field: 'vehicles',
        headerName: 'Vehicles',
        flex: 1,
        minWidth: 80,
        valueGetter: (params: ValueGetterParams) => params.data?.vehicles?.length || 0,
        cellRenderer: VehiclesCellRenderer,
      },
      {
        field: 'overallStatus',
        headerName: 'Status',
        flex: 1.2,
        minWidth: 120,
        filter: 'agTextColumnFilter',
        cellRenderer: StatusCellRenderer,
      },
      {
        field: 'progress',
        headerName: 'Progress',
        flex: 1.5,
        minWidth: 120,
        sortable: false,
        cellRenderer: ProgressCellRenderer,
      },
      {
        field: 'submissionCount',
        headerName: 'Photos',
        flex: 1,
        minWidth: 90,
        cellRenderer: PhotosCellRenderer,
        cellStyle: { display: 'flex', alignItems: 'center' }
      },
      {
        field: 'videoCount',
        headerName: 'Videos',
        flex: 1,
        minWidth: 90,
        cellRenderer: VideosCellRenderer,
        cellStyle: { display: 'flex', alignItems: 'center' }
      },
      {
        field: 'accessCodes',
        headerName: 'Access Code Status',
        flex: 1.5,
        minWidth: 180,
        sortable: false,
        cellRenderer: AccessCodesSummaryRenderer,
        cellStyle: { display: 'flex', alignItems: 'center' }
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        cellRenderer: ActionsRenderer,
        cellStyle: { display: 'flex', alignItems: 'center' }
      },
    ],
    [ContactCellRenderer, VehiclesCellRenderer, StatusCellRenderer, ProgressCellRenderer, PhotosCellRenderer, VideosCellRenderer, AccessCodesSummaryRenderer, ActionsRenderer],
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    [],
  )

  const isRowMaster = useMemo(() => {
    return (dataItem: any) => {
      return dataItem ? dataItem.vehicles?.length > 0 : false
    }
  }, [])

  // Use auto height for detail rows to accommodate variable vehicle counts
  const detailRowAutoHeight = true

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        columnDefs: [
          {
            field: 'vehicle',
            headerName: 'Vehicle Details',
            flex: 2,
            cellRenderer: (cellParams: { data: UserPhotoUploadSummary['vehicles'][0] }) => (
              <Box sx={{ py: 1 }}>
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    setVehicleDialogId(cellParams.data.id)
                    setVehicleDialogOpen(true)
                  }}
                >
                  {cellParams.data.year} {cellParams.data.make} {cellParams.data.model}
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  VIN: {cellParams.data.vin} • Stock #: {cellParams.data.stock_number || 'N/A'}
                </Typography>
              </Box>
            ),
          },
          {
            field: 'submissionStatus',
            headerName: 'Status',
            flex: 1,
            cellRenderer: (cellParams: { data: UserPhotoUploadSummary['vehicles'][0] }) => {
              const vehicle = cellParams.data
              const photoCount = vehicle.photoCount || 0
              const isComplete = photoCount >= REQUIRED_PHOTOS

              return (
                <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size='small'
                    label={isComplete ? 'Complete' : `${photoCount}/${REQUIRED_PHOTOS} photos`}
                    color={isComplete ? 'success' : photoCount > 0 ? 'warning' : 'error'}
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                </Box>
              )
            },
          },
        ],
        defaultColDef: {
          sortable: true,
          resizable: true,
          suppressSizeToFit: false,
        },
        domLayout: 'autoHeight' as const,
        suppressHorizontalScroll: true,
        headerHeight: 40,
        rowHeight: 60,
      },
      getDetailRowData: (params: DetailGridParams) => {
        params.successCallback(params.data.vehicles || [])
      },
    }),
    [],
  )

  // Auto-expand matching rows when searching so vehicles are visible
  useEffect(() => {
    const api = gridApiRef.current
    if (!api) return
    // Collapse all first
    api.forEachNode((node: any) => node.setExpanded(false))
    const term = searchTerm.trim().toLowerCase()
    if (!term) return
    api.forEachNode((node: any) => {
      const data: UserPhotoUploadSummary = node.data
      const matches =
        data.user.email?.toLowerCase().includes(term) ||
        data.user.phone?.toLowerCase().includes(term) ||
        data.vehicles?.some(v =>
          `${v.make} ${v.model} ${v.year} ${v.vin} ${v.stock_number || ''}`.toLowerCase().includes(term),
        )
      if (matches) {
        node.setExpanded(true)
      }
    })
  }, [searchTerm])

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return
    setCollapsedUploadIds(prev => {
      const next = new Set(prev)
      filteredPhotoUploads.forEach(upload => {
        const vehicleMatch = upload.vehicles?.some(v =>
          `${v.make} ${v.model} ${v.year} ${v.vin} ${v.stock_number || ''}`.toLowerCase().includes(term),
        )
        if (vehicleMatch) {
          next.delete(upload.photoUploadId)
        }
      })
      return next
    })
  }, [searchTerm, filteredPhotoUploads])

  const toggleUploadExpanded = (photoUploadId: string) => {
    setCollapsedUploadIds(prev => {
      const next = new Set(prev)
      if (next.has(photoUploadId)) {
        next.delete(photoUploadId)
      } else {
        next.add(photoUploadId)
      }
      return next
    })
  }

  const MobileCardView = () => (
    <Stack spacing={2} sx={{ p: 2, overflowX: 'hidden', maxWidth: '100%' }}>
      <MobileRecordList isEmpty={filteredPhotoUploads.length === 0} empty='No photo uploads match your filters.'>
      {filteredPhotoUploads.map(upload => {
        const progress = calculateProgress(upload)
        const statusLabel = upload.overallStatus

        return (
          <Box key={upload.photoUploadId}>
            <MobileRecordCard
              title={upload.user.email}
              status={{ tone: photoUploadStatusTone(statusLabel), label: statusLabel }}
              meta={upload.user.phone || 'No phone'}
              detail={`${upload.vehicles?.length || 0} vehicles · ${upload.actualPhotoCount !== undefined ? upload.actualPhotoCount : upload.submissionCount}/${upload.expectedPhotoCount !== undefined ? upload.expectedPhotoCount : upload.totalVehicles} photos · ${upload.actualVideoCount || 0}/${upload.expectedVideoCount || upload.totalVehicles} videos`}
            />

              <Box sx={{ mb: 2, px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant='determinate'
                    value={progress}
                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    color={progress === 100 ? 'success' : progress > 0 ? 'primary' : 'inherit'}
                  />
                  <Typography variant='caption' sx={{ minWidth: 35, textAlign: 'right' }}>
                    {progress}%
                  </Typography>
                </Box>
              </Box>

              {upload.vehicles?.length ? (
                <>
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => toggleUploadExpanded(upload.photoUploadId)}
                    endIcon={collapsedUploadIds.has(upload.photoUploadId) ? <ExpandMore /> : <ExpandLess />}
                    sx={{ mb: 1, px: 0, justifyContent: 'flex-start' }}
                  >
                    {collapsedUploadIds.has(upload.photoUploadId) ? 'Show vehicles' : 'Hide vehicles'}
                  </Button>
                  <Collapse in={!collapsedUploadIds.has(upload.photoUploadId)}>
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {upload.vehicles.map(vehicle => {
                        const photoCount = vehicle.photoCount || 0
                        const isComplete = photoCount >= REQUIRED_PHOTOS
                        return (
                          <Box
                            key={vehicle.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{ fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => {
                                setVehicleDialogId(vehicle.id)
                                setVehicleDialogOpen(true)
                              }}
                            >
                              {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                            </Typography>
                            <Typography variant='caption' color='text.secondary' display='block'>
                              VIN: {vehicle.vin || 'N/A'} · Stock #: {vehicle.stock_number || 'N/A'}
                            </Typography>
                            <StatusChip
                              dense
                              tone={isComplete ? 'success' : photoCount > 0 ? 'warning' : 'danger'}
                              label={isComplete ? 'Complete' : `${photoCount}/${REQUIRED_PHOTOS} photos`}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                  </Collapse>
                </>
              ) : null}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<PhotoAlbumOutlined />}
                  onClick={() => {
                    setSelectedPhotoUploadId(upload.photoUploadId)
                    setIsViewDialogOpen(true)
                  }}
                  disabled={!upload.vehicles?.length}
                  fullWidth
                  sx={{ minHeight: TOUCH_MIN }}
                >
                  View Photos
                </Button>
                <Button
                  variant='text'
                  size='small'
                  startIcon={<Settings />}
                  onClick={() => {
                    setSelectedUserSummary(upload)
                    setIsManageDialogOpen(true)
                  }}
                  fullWidth
                  sx={{ minHeight: TOUCH_MIN }}
                >
                  Manage
                </Button>
              </Box>
          </Box>
        )
      })}
      </MobileRecordList>
    </Stack>
  )

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error loading data: {(error as Error).message}
      </Box>
    )
  }

  return (
    <AdminPageFrame
      title='Photo Uploader'
      subtitle='Track photo documentation for truck inspections and assessments'
      action={
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setIsInitiateDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
          aria-label="Send new link"
        >
          Send New Link
        </Button>
      }
    >

      {/* Overview Stats Cards */}
      <PhotoUploadOverview photoUploads={photoUploads} isLoading={isLoading} />

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder='Search by name, email, phone, VIN, make/model, or stock number...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                  label='Status Filter'
                >
                  <MenuItem value='all'>All Status</MenuItem>
                  <MenuItem value='not_submitted'>Pending</MenuItem>
                  <MenuItem value='partially_submitted'>In Progress</MenuItem>
                  <MenuItem value='complete'>Complete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant='outlined'
                  startIcon={<History />}
                  onClick={() => setIsHistoryDialogOpen(true)}
                  fullWidth
                >
                  History
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Display */}
      {isLoading ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography>Loading photo upload data...</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {isMobile ? (
            <MobileCardView />
          ) : (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box className='ag-theme-alpine' sx={{ height: 600, width: '100%' }}>
                  <AgGridReact
                    rowData={filteredPhotoUploads}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={20}
                    suppressCellFocus={true}
                    masterDetail={true}
                    detailCellRendererParams={detailCellRendererParams}
                    isRowMaster={isRowMaster}
                    detailRowAutoHeight={detailRowAutoHeight}
                    overlayNoRowsTemplate="<span style='padding: 10px; font-size: 16px;'>No photo uploads found matching your criteria</span>"
                    overlayLoadingTemplate="<span style='padding: 10px; font-size: 16px;'>Loading photo uploads...</span>"
                    loading={isLoading}
                    headerHeight={50}
                    rowHeight={70}
                    enableCellTextSelection={true}
                    suppressRowClickSelection={true}
                    suppressRowDeselection={true}
                    onGridReady={(params) => {
                      gridApiRef.current = params.api
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Dialogs */}
      <ViewPhotoSubmissionsDialog
        open={isViewDialogOpen}
        onClose={() => {
          setIsViewDialogOpen(false)
          setSelectedPhotoUploadId(null)
        }}
        photoUploadId={selectedPhotoUploadId}
      />

      {selectedUserSummary && (
        <ManageAccessCodesDialog
          open={isManageDialogOpen}
          onClose={() => {
            setIsManageDialogOpen(false)
            setSelectedUserSummary(null)
          }}
          userSummary={selectedUserSummary}
        />
      )}

      <CreatePhotoUploadDialog open={isInitiateDialogOpen} onClose={() => setIsInitiateDialogOpen(false)} />

      <PhotoUploadHistory open={isHistoryDialogOpen} onClose={() => setIsHistoryDialogOpen(false)} />

      {vehicleDialogId && (
        <ReadOnlyVehicleForm
          vehicleId={vehicleDialogId}
          open={vehicleDialogOpen}
          onClose={() => setVehicleDialogOpen(false)}
          inventoryCollection={'master_inventory'}
        />
      )}
    </AdminPageFrame>
  )
}

export default PhotoUpload
